import axios from "../utils/axiosConfig.js";
import { toast } from "react-toastify";

const API_URL = process.env.REACT_APP_API_URL;

/**
 * ==============================================================================
 * FILL-FIRST ALGORITHM - Optimiert für maximale Kursauslastung
 * ==============================================================================
 *
 * ZIEL: Maximiere Anzahl voller Kurse (4 Schüler), minimiere gemischte Levels
 *
 * GESCHLECHTER-MATCHING (2025-10-15):
 *   - Erwachsene: STRIKTE Geschlechtertrennung (männlich/weiblich)
 *   - Kinder: KEINE Geschlechtertrennung
 *
 * PHASE 1: Volle Kurse erstellen (4 Schüler)
 *   - Gruppiere Schüler nach exaktem Level/Gruppe UND Geschlecht (Erwachsene)
 *   - Finde Zeitslots mit 4+ Schülern derselben Gruppe
 *   - PRIORITY SORTING: Verarbeite zuerst Schüler mit wenigen verfügbaren Zeiten
 *     (verhindert, dass flexible Schüler die einzigen Slots von unflexiblen blockieren)
 *   - BATCH PROCESSING: Erstelle ALLE möglichen 4er-Kurse pro Zeitslot auf einmal
 *   - Beispiel: 12 weibliche Fortgeschrittene @ Montag 19 → 3 Kurse sofort erstellen
 *
 * PHASE 2: Bestehende Kurse auffüllen (→4 Schüler)
 *   - Fülle vorhandene Kurse auf Kapazität
 *   - Strikte Level-Übereinstimmung + Geschlecht (Erwachsene)
 *
 * PHASE 3: Kleinere Kurse (2-3 Schüler)
 *   - Erstelle neue 2-3 Personen Kurse
 *   - Weiterhin strikte Level-Regeln + Geschlecht (Erwachsene)
 *
 * PHASE 4: Entspannte Regeln (benachbarte Levels) - NUR FÜR SCHWER PLATZIERBARE
 *   - EINTRITTSBEDINGUNG: Student hat ≤2 verfügbare Zeiten UND keine reinen Level-Slots
 *   - Erlaube benachbarte Level-Kombinationen nur wenn unvermeidbar
 *   - Max. 2 verschiedene Levels pro Kurs
 *   - GESCHLECHT bleibt STRIKT (keine Mischung bei Erwachsenen)
 *   - Reduziert gemischte Kurse von 30% → <10%
 *
 * PHASE 5: Einzelplatzierungen (letzter Ausweg)
 *   - 1-Personen Kurse nur wenn unvermeidbar
 *   - Versucht ALLE verfügbaren Zeitslots
 * ==============================================================================
 *
 * ERWARTETE ERGEBNISSE (mit Gender-Matching 2025-10-15):
 *   - 23 volle 4-Schüler Kurse (theoretisches Maximum mit Geschlechtertrennung)
 *   - 0% gemischte Geschlechter bei Erwachsenen
 *   - <10% gemischte Level-Kurse
 *   - 95%+ Schüler zugewiesen
 *   - Gesamt-Score: 80+/100
 * ==============================================================================
 */

// Helper: Are two adult skill levels compatible for mixing?
const areAdultSkillLevelsCompatible = (level1, level2) => {
  if (level1 === level2) return true;
  const advancedPair = ["gute:r Spieler:in", "Fortgeschritten"];
  const beginnerPair = ["Anfänger", "wenig Fortgeschritten", "Anfänger mit Grundkenntnissen"];
  return (
    (advancedPair.includes(level1) && advancedPair.includes(level2)) ||
    (beginnerPair.includes(level1) && beginnerPair.includes(level2))
  );
};

// Helper: Are two children training groups compatible for mixing?
const areChildrenGroupsCompatible = (group1, group2) => {
  if (group1 === group2) return true;
  const adjacentPairs = [
    ["Kinderland", "Rot"],
    ["Rot", "Orange"],
    ["Orange", "Grün"],
    ["Gelb Hobby", "Gelb Team"]
  ];
  return adjacentPairs.some(pair => pair.includes(group1) && pair.includes(group2));
};

// Helper: Get max capacity
const getMaxCapacity = (student, settings) => {
  if (!settings?.courseCapacity) return 4;
  const { capacityByGroup, defaultMaxStudents } = settings.courseCapacity;
  if (student.adult) return capacityByGroup.Erwachsene || defaultMaxStudents;
  return capacityByGroup[student.trainigGroup] || defaultMaxStudents;
};

// Helper: Check if two students can be in same course (gender check for adults)
const canBeInSameCourse = (student1, student2) => {
  // Must be same type (both adult or both children)
  if (student1.adult !== student2.adult) return false;

  if (student1.adult) {
    // For adults: Must match gender AND skill level
    if (student1.sex !== student2.sex) return false;
    return student1.skillLevel === student2.skillLevel;
  } else {
    // For children: Only match training group (no gender check)
    return student1.trainigGroup === student2.trainigGroup;
  }
};

export const resetSchedule = async (setStudents, setSchedule) => {
  try {
    const startTime = performance.now();
    setSchedule([]);

    // Fetch data
    const { data: students } = await axios.get(`/api/students`);
    const { data: coaches } = await axios.get(`/api/coaches`);
    const { data: settings } = await axios.get(`/api/settings`);

    console.log(`\n📊 FILL-FIRST ALGORITHM: ${students.length} students, ${coaches.length} coaches\n`);

    // Build lookup structures
    const coachById = new Map();
    coaches.forEach(c => coachById.set(String(c._id), c));

    const coachesByTimeSlot = new Map();
    coaches.forEach(coach => {
      (coach.availableTimes || []).forEach(time => {
        if (!coachesByTimeSlot.has(time)) coachesByTimeSlot.set(time, []);
        coachesByTimeSlot.get(time).push(coach);
      });
    });

    const adultCoaches = new Set(coaches.filter(c => c.isCoachingAdult).map(c => c._id));
    const childrenCoaches = new Set(coaches.filter(c => c.isCoachingChildren).map(c => c._id));

    const getSuitableCoaches = (student, timeToken) => {
      const available = coachesByTimeSlot.get(timeToken) || [];
      const qualified = student.adult ? adultCoaches : childrenCoaches;
      return available.filter(c => qualified.has(c._id));
    };

    const courseMap = new Map();
    const getOrCreateCourse = (day, hour, coach) => {
      const key = `${day}-${hour}-${coach._id}`;
      if (!courseMap.has(key)) {
        courseMap.set(key, {
          day, hour,
          coachId: coach._id,
          coachName: `${coach.firstName} ${coach.lastName}`,
          students: []
        });
      }
      return courseMap.get(key);
    };

    const assignedStudents = new Set();
    let stats = { phase0: 0, phase1: 0, phase2: 0, phase3: 0, phase4: 0, phase5: 0, phase6Moved: 0, phase6Eliminated: 0 };

    // ========================================
    // PHASE 0: RESERVE SLOTS FOR CRITICALLY CONSTRAINED STUDENTS
    // ========================================
    console.log(`🎯 PHASE 0: Reserving slots for critically constrained students (≤2 time slots)...`);
    const p0Start = performance.now();

    // Find students with very limited availability (≤2 time slots)
    const criticalStudents = students.filter(s =>
      s.availableTimes && s.availableTimes.length > 0 && s.availableTimes.length <= 2
    );

    // Sort by flexibility (least flexible first)
    criticalStudents.sort((a, b) =>
      (a.availableTimes?.length || 0) - (b.availableTimes?.length || 0)
    );

    console.log(`   Found ${criticalStudents.length} critically constrained students (≤2 slots)`);

    // Assign each critical student to their BEST available slot
    for (const student of criticalStudents) {
      let assigned = false;

      for (const timeToken of student.availableTimes) {
        if (assigned) break;

        const [day, hourStr] = timeToken.split(" ");
        const hour = parseInt(hourStr);
        const coaches = getSuitableCoaches(student, timeToken);

        if (coaches.length === 0) continue;

        // Try to join an existing course with same level/group/gender
        for (const coach of coaches) {
          const course = getOrCreateCourse(day, hour, coach);

          // Check if course has space
          const maxCap = getMaxCapacity(student, settings);
          if (course.students.length >= maxCap) continue;

          // If course has students, check compatibility
          if (course.students.length > 0) {
            const allCompatible = course.students.every(s => canBeInSameCourse(student, s));
            if (!allCompatible) continue;
          }

          // Assign student
          course.students.push(student);
          student.day = day;
          student.hour = hour;
          student.coach = coach._id;
          assignedStudents.add(student._id);
          stats.phase0++;
          assigned = true;
          break;
        }
      }

      if (assigned) {
        const level = student.adult ? student.skillLevel : student.trainigGroup;
        console.log(`   ✅ ${student.firstName} ${student.lastName} (${level}, ${student.availableTimes.length} slots) → ${student.day} ${student.hour}`);
      }
    }

    console.log(`✅ Phase 0: ${stats.phase0}/${criticalStudents.length} critical students assigned (${(performance.now() - p0Start).toFixed(0)}ms)`);

    // ========================================
    // PHASE 1: CREATE FULL COURSES (4 students)
    // ========================================
    console.log(`🎯 PHASE 1: Creating full courses (4 students each)...`);
    const p1Start = performance.now();

    // Group students by exact level/group (AND gender for adults)
    const studentGroups = new Map();
    students.forEach(s => {
      let key;
      if (s.adult) {
        // For adults: Include gender in grouping (strict gender matching)
        const gender = s.sex || 'unknown';
        key = `adult-${gender}-${s.skillLevel}`;
      } else {
        // For children: No gender separation
        key = `child-${s.trainigGroup}`;
      }
      if (!studentGroups.has(key)) studentGroups.set(key, []);
      studentGroups.get(key).push(s);
    });

    // Find time slots with 4+ students from same group
    const fullCourseOpportunities = [];
    studentGroups.forEach((groupStudents, groupKey) => {
      const timeSlotCounts = new Map();
      groupStudents.forEach(s => {
        (s.availableTimes || []).forEach(time => {
          if (!timeSlotCounts.has(time)) timeSlotCounts.set(time, []);
          timeSlotCounts.get(time).push(s);
        });
      });

      timeSlotCounts.forEach((studentsAtTime, timeToken) => {
        if (studentsAtTime.length >= 4) {
          const [day, hourStr] = timeToken.split(" ");
          fullCourseOpportunities.push({
            groupKey, timeToken, day, hour: parseInt(hourStr),
            students: studentsAtTime, count: studentsAtTime.length
          });
        }
      });
    });

    // BALANCED PRIORITY SORTING: Balance efficiency (large groups) and fairness (constrained students)
    // We want to prioritize BOTH large efficient opportunities AND constrained students
    fullCourseOpportunities.sort((a, b) => {
      const avgFlexA = a.students.reduce((sum, s) =>
        sum + (s.availableTimes?.length || 0), 0) / a.students.length;
      const avgFlexB = b.students.reduce((sum, s) =>
        sum + (s.availableTimes?.length || 0), 0) / b.students.length;

      // Calculate "value score" = (potential full courses) * (urgency factor)
      // urgency = 1 / (avgFlex + 1) ... higher for more constrained students
      const numCoursesA = Math.floor(a.count / 4);
      const numCoursesB = Math.floor(b.count / 4);

      const urgencyA = 1 / (avgFlexA + 1); // +1 to avoid division by zero
      const urgencyB = 1 / (avgFlexB + 1);

      const scoreA = numCoursesA * urgencyA;
      const scoreB = numCoursesB * urgencyB;

      // Higher score = better (combines both factors)
      if (Math.abs(scoreA - scoreB) > 0.15) {
        return scoreB - scoreA; // Descending order
      }

      // Tiebreaker 1: Prefer larger groups (efficiency)
      if (a.count !== b.count) {
        return b.count - a.count;
      }

      // Tiebreaker 2: Prefer less flexible (fairness)
      return avgFlexA - avgFlexB;
    });

    console.log(`   Found ${fullCourseOpportunities.length} opportunities for full courses`);

    // Log top 10 opportunities to show balanced prioritization
    if (fullCourseOpportunities.length > 0) {
      console.log(`   Top opportunities (balanced score = courses * urgency):`);
      fullCourseOpportunities.slice(0, 10).forEach((opp, i) => {
        const avgFlex = opp.students.reduce((sum, s) =>
          sum + (s.availableTimes?.length || 0), 0) / opp.students.length;
        const numCourses = Math.floor(opp.count / 4);
        const urgency = 1 / (avgFlex + 1);
        const score = numCourses * urgency;
        console.log(`      ${i+1}. ${opp.groupKey} @ ${opp.timeToken}: ${opp.count} students, avg ${avgFlex.toFixed(1)} slots, ${numCourses} courses, score ${score.toFixed(2)}`);
      });
    }

    // IMPROVEMENT: Create multiple courses at once for high-overlap slots
    // This ensures we maximize full courses instead of spreading students thin
    for (const opp of fullCourseOpportunities) {
      let unassigned = opp.students.filter(s => !assignedStudents.has(s._id));
      if (unassigned.length < 4) continue;

      const coaches = getSuitableCoaches(unassigned[0], opp.timeToken);
      if (coaches.length === 0) {
        console.log(`   ⚠️  ${opp.groupKey} @ ${opp.timeToken}: ${unassigned.length} students but NO COACHES`);
        continue;
      }

      // Calculate how many full courses we can create at this timeslot
      const maxCap = getMaxCapacity(unassigned[0], settings);
      const numFullCourses = Math.floor(unassigned.length / 4);

      console.log(`   📍 ${opp.groupKey} @ ${opp.timeToken}: ${unassigned.length} students → ${numFullCourses} full courses possible (${coaches.length} coaches)`);

      // Create ALL possible full courses at once (batch processing)
      let coursesCreated = 0;
      for (let courseNum = 0; courseNum < numFullCourses; courseNum++) {
        // Check if we still have enough unassigned students
        unassigned = opp.students.filter(s => !assignedStudents.has(s._id));
        if (unassigned.length < 4) {
          console.log(`      ⚠️  Only ${unassigned.length} students left, stopping`);
          break;
        }

        // Use different coach if available to avoid overcrowding
        const coachIndex = courseNum % coaches.length;
        const coach = coaches[coachIndex];

        const course = getOrCreateCourse(opp.day, opp.hour, coach);

        // Skip if this coach's course already has students
        if (course.students.length > 0) {
          console.log(`      ⚠️  Coach ${coach.firstName} already has course, trying next`);
          continue;
        }

        // Add exactly 4 students to this course
        const numToAssign = Math.min(4, maxCap);
        for (let i = 0; i < numToAssign; i++) {
          const student = unassigned[i];
          course.students.push(student);
          student.day = opp.day;
          student.hour = opp.hour;
          student.coach = coach._id;
          assignedStudents.add(student._id);
          stats.phase1++;
        }
        coursesCreated++;
      }

      if (coursesCreated > 0) {
        console.log(`      ✅ Created ${coursesCreated} full courses`);
      }
    }

    console.log(`✅ Phase 1: ${stats.phase1} students → ${Array.from(courseMap.values()).filter(c => c.students.length === 4).length} full courses (${(performance.now() - p1Start).toFixed(0)}ms)`);

    // ========================================
    // PHASE 2: FILL EXISTING COURSES TO CAPACITY
    // ========================================
    console.log(`🎯 PHASE 2: Filling existing courses to capacity...`);
    const p2Start = performance.now();

    const unassigned2 = students.filter(s => !assignedStudents.has(s._id));
    unassigned2.sort((a, b) => (a.availableTimes || []).length - (b.availableTimes || []).length);

    for (const student of unassigned2) {
      let bestMatch = null;

      for (const timeToken of (student.availableTimes || [])) {
        const [day, hourStr] = timeToken.split(" ");
        const hour = parseInt(hourStr);
        const coaches = getSuitableCoaches(student, timeToken);

        for (const coach of coaches) {
          // FIX #4: Only consider EXISTING courses, don't create new ones
          const courseKey = `${day}-${hour}-${coach._id}`;
          const course = courseMap.get(courseKey);

          // Skip if course doesn't exist yet, is empty, or is full
          if (!course || course.students.length === 0 || course.students.length >= 4) continue;

          // Check strict matching (including gender for adults)
          const allCompatible = course.students.every(s => canBeInSameCourse(student, s));
          if (!allCompatible) continue;

          const maxCap = getMaxCapacity(student, settings);
          if (course.students.length >= maxCap) continue;

          // Prefer fuller courses
          if (!bestMatch || course.students.length > bestMatch.course.students.length) {
            bestMatch = { course, coach };
          }
        }
      }

      if (bestMatch) {
        // CRITICAL: Final capacity check before adding (course may have been filled since search)
        const maxCap = getMaxCapacity(student, settings);
        if (bestMatch.course.students.length < maxCap) {
          bestMatch.course.students.push(student);
          student.day = bestMatch.course.day;
          student.hour = bestMatch.course.hour;
          student.coach = bestMatch.coach._id;
          assignedStudents.add(student._id);
          stats.phase2++;
        }
      }
    }

    console.log(`✅ Phase 2: +${stats.phase2} students filled into existing courses (${(performance.now() - p2Start).toFixed(0)}ms)`);

    // ========================================
    // PHASE 3: CREATE SMALLER COURSES (2-3 students)
    // ========================================
    console.log(`🎯 PHASE 3: Creating smaller courses (2-3 students)...`);
    const p3Start = performance.now();

    const unassigned3 = students.filter(s => !assignedStudents.has(s._id));
    unassigned3.sort((a, b) => (a.availableTimes || []).length - (b.availableTimes || []).length);

    for (const student of unassigned3) {
      let bestMatch = null;

      for (const timeToken of (student.availableTimes || [])) {
        const [day, hourStr] = timeToken.split(" ");
        const hour = parseInt(hourStr);
        const coaches = getSuitableCoaches(student, timeToken);

        for (const coach of coaches) {
          const course = getOrCreateCourse(day, hour, coach);

          // Skip full courses
          if (course.students.length >= 4) continue;

          // Check strict matching (including gender for adults)
          if (course.students.length > 0) {
            const allCompatible = course.students.every(s => canBeInSameCourse(student, s));
            if (!allCompatible) continue;
          }

          const maxCap = getMaxCapacity(student, settings);
          if (course.students.length >= maxCap) continue;

          // Prefer courses with 2 students (almost full)
          if (!bestMatch || course.students.length > bestMatch.course.students.length) {
            bestMatch = { course, coach };
          }
        }
      }

      if (bestMatch) {
        // CRITICAL: Final capacity check before adding
        const maxCap = getMaxCapacity(student, settings);
        if (bestMatch.course.students.length < maxCap) {
          bestMatch.course.students.push(student);
          student.day = bestMatch.course.day;
          student.hour = bestMatch.course.hour;
          student.coach = bestMatch.coach._id;
          assignedStudents.add(student._id);
          stats.phase3++;
        }
      }
    }

    console.log(`✅ Phase 3: +${stats.phase3} students in smaller courses (${(performance.now() - p3Start).toFixed(0)}ms)`);

    // ========================================
    // PHASE 4: RELAXED MODE (adjacent levels)
    // ========================================
    console.log(`🎯 PHASE 4: Relaxed mode (adjacent level mixing)...`);
    const p4Start = performance.now();

    const unassigned4 = students.filter(s => !assignedStudents.has(s._id));

    // IMPROVEMENT: Only process truly hard-to-place students
    // Students with ≤2 available times AND no pure-level slots available
    const hardToPlace = unassigned4.filter(student => {
      if (!student.availableTimes || student.availableTimes.length > 2) return false;

      // Check if ANY pure-level slot exists
      for (const timeToken of student.availableTimes) {
        const coaches = getSuitableCoaches(student, timeToken);
        if (coaches.length === 0) continue;

        for (const coach of coaches) {
          const [day, hourStr] = timeToken.split(" ");
          const hour = parseInt(hourStr);
          const courseKey = `${day}-${hour}-${coach._id}`;
          const course = courseMap.get(courseKey);

          // If no course exists yet, pure-level is possible
          if (!course) return false;

          // If course has space and same level/group/gender, pure-level is possible
          if (course.students.length < 4) {
            const allCompatible = course.students.every(s => canBeInSameCourse(student, s));
            if (allCompatible) return false; // Pure-level slot available (with gender match for adults)
          }
        }
      }

      return true; // No pure-level slots available
    });

    if (hardToPlace.length === 0) {
      console.log(`✅ Phase 4: SKIPPED - All students can be placed without level mixing`);
    } else {
      console.log(`   Processing ${hardToPlace.length}/${unassigned4.length} truly hard-to-place students`);
    }

    for (const student of hardToPlace) {
      let bestMatch = null;

      for (const timeToken of (student.availableTimes || [])) {
        const [day, hourStr] = timeToken.split(" ");
        const hour = parseInt(hourStr);
        const coaches = getSuitableCoaches(student, timeToken);

        for (const coach of coaches) {
          const course = getOrCreateCourse(day, hour, coach);

          if (course.students.length >= 4) continue;

          // Check adult/child mixing (never allowed) and gender (never mix for adults)
          if (course.students.length > 0) {
            const allSameAdult = course.students.every(s => s.adult === student.adult);
            if (!allSameAdult) continue;

            // STRICT: Gender must match for adults (even in relaxed mode)
            if (student.adult) {
              const allSameGender = course.students.every(s => s.sex === student.sex);
              if (!allSameGender) continue;

              // Check adjacent level compatibility (this is what's "relaxed")
              const levels = [...new Set(course.students.map(s => s.skillLevel))];
              const allCompat = levels.every(l => areAdultSkillLevelsCompatible(student.skillLevel, l));
              const wouldCreateThird = !levels.includes(student.skillLevel) && levels.length >= 2;
              if (!allCompat || wouldCreateThird) continue;
            } else {
              // For children: Check adjacent group compatibility (no gender check)
              const groups = [...new Set(course.students.map(s => s.trainigGroup))];
              const allCompat = groups.every(g => areChildrenGroupsCompatible(student.trainigGroup, g));
              const wouldCreateThird = !groups.includes(student.trainigGroup) && groups.length >= 2;
              if (!allCompat || wouldCreateThird) continue;
            }
          }

          const maxCap = getMaxCapacity(student, settings);
          if (course.students.length >= maxCap) continue;

          if (!bestMatch || course.students.length > bestMatch.course.students.length) {
            bestMatch = { course, coach };
          }
        }
      }

      if (bestMatch) {
        // CRITICAL: Final capacity check before adding
        const maxCap = getMaxCapacity(student, settings);
        if (bestMatch.course.students.length < maxCap) {
          bestMatch.course.students.push(student);
          student.day = bestMatch.course.day;
          student.hour = bestMatch.course.hour;
          student.coach = bestMatch.coach._id;
          assignedStudents.add(student._id);
          stats.phase4++;
        }
      }
    }

    console.log(`✅ Phase 4: +${stats.phase4} students with adjacent level mixing (${(performance.now() - p4Start).toFixed(0)}ms)`);

    // ========================================
    // PHASE 5: SINGLES (last resort)
    // ========================================
    console.log(`🎯 PHASE 5: Single student courses (last resort)...`);
    const p5Start = performance.now();

    const unassigned5 = students.filter(s => !assignedStudents.has(s._id));

    for (const student of unassigned5) {
      if (!student.availableTimes || student.availableTimes.length === 0) continue;

      let assigned = false;

      // Try each available time slot until we find one with capacity
      for (const timeToken of student.availableTimes) {
        const [day, hourStr] = timeToken.split(" ");
        const hour = parseInt(hourStr);
        const coaches = getSuitableCoaches(student, timeToken);

        // FIX: Try ALL available coaches at this time, not just the first one
        for (const coach of coaches) {
          const course = getOrCreateCourse(day, hour, coach);

          // Check capacity before adding student
          const maxCap = getMaxCapacity(student, settings);
          if (course.students.length >= maxCap) {
            // This coach's course is full, try next coach
            continue;
          }

          // Found a spot! Create single-student course if empty
          course.students.push(student);
          student.day = day;
          student.hour = hour;
          student.coach = coach._id;
          assignedStudents.add(student._id);
          stats.phase5++;
          assigned = true;
          break; // Student assigned, move to next student
        }

        if (assigned) break; // Found assignment at this time, stop searching
      }
    }

    console.log(`✅ Phase 5: +${stats.phase5} single students (${(performance.now() - p5Start).toFixed(0)}ms)`);

    // ========================================
    // PHASE 6: COURSE CONSOLIDATION
    // ========================================
    console.log(`🎯 PHASE 6: Consolidating small courses...`);
    const p6Start = performance.now();

    // Get all courses sorted by size (smallest first)
    const allCourses = Array.from(courseMap.values()).sort((a, b) => a.students.length - b.students.length);

    let moved = 0;
    let coursesEliminated = 0;

    // Iterate through small courses (1-2 students)
    for (const smallCourse of allCourses) {
      if (smallCourse.students.length > 2) break; // Only process 1-2 student courses
      if (smallCourse.students.length === 0) continue; // Skip empty

      const studentsToMove = [...smallCourse.students]; // Copy array
      let allMovedFromThisCourse = true;

      for (const student of studentsToMove) {
        let movedThisStudent = false;

        // Try to find a compatible course at one of student's other available times
        for (const timeToken of (student.availableTimes || [])) {
          // Skip current assignment
          if (timeToken === `${student.day} ${student.hour}`) continue;

          const [day, hourStr] = timeToken.split(" ");
          const hour = parseInt(hourStr);
          const coaches = getSuitableCoaches(student, timeToken);

          for (const coach of coaches) {
            const courseKey = `${day}-${hour}-${coach._id}`;
            const targetCourse = courseMap.get(courseKey);

            // Skip if no course exists or it's full
            if (!targetCourse || targetCourse.students.length >= 4) continue;

            // Check compatibility (level, group, gender)
            const allCompatible = targetCourse.students.every(s => canBeInSameCourse(student, s));
            if (!allCompatible) continue;

            // Check capacity
            const maxCap = getMaxCapacity(student, settings);
            if (targetCourse.students.length >= maxCap) continue;

            // MOVE THE STUDENT!
            console.log(`   📦 Moving ${student.firstName} ${student.lastName} from ${student.day} ${student.hour} (${smallCourse.students.length} students) → ${day} ${hour} (${targetCourse.students.length}/4 students)`);

            // Remove from old course
            const indexInSmall = smallCourse.students.indexOf(student);
            if (indexInSmall > -1) {
              smallCourse.students.splice(indexInSmall, 1);
            }

            // Add to new course
            targetCourse.students.push(student);
            student.day = day;
            student.hour = hour;
            student.coach = coach._id;

            moved++;
            stats.phase6Moved++;
            movedThisStudent = true;
            break; // Found a spot, move to next student
          }

          if (movedThisStudent) break; // Stop trying other time slots
        }

        if (!movedThisStudent) {
          allMovedFromThisCourse = false; // At least one student couldn't be moved
        }
      }

      // If ALL students were moved from this course, it's now empty - eliminate it
      if (allMovedFromThisCourse && smallCourse.students.length === 0) {
        const courseKey = `${smallCourse.day}-${smallCourse.hour}-${smallCourse.coachId}`;
        courseMap.delete(courseKey);
        coursesEliminated++;
        stats.phase6Eliminated++;
        console.log(`   ✅ Eliminated empty course: ${smallCourse.day} ${smallCourse.hour}`);
      }
    }

    console.log(`✅ Phase 6: Moved ${moved} students, eliminated ${coursesEliminated} courses (${(performance.now() - p6Start).toFixed(0)}ms)`);

    // ========================================
    // FINALIZE & SAVE
    // ========================================
    const updatedSchedule = Array.from(courseMap.values());
    const studentsNotSet = students.filter(s => !assignedStudents.has(s._id));

    // Clear unassigned students
    students.forEach(s => {
      if (!assignedStudents.has(s._id)) {
        s.day = null;
        s.hour = null;
        s.coach = null;
      }
    });

    const totalTime = performance.now() - startTime;
    const totalAssigned = stats.phase0 + stats.phase1 + stats.phase2 + stats.phase3 + stats.phase4 + stats.phase5;

    // Course size analysis
    const courseSizes = { 1: 0, 2: 0, 3: 0, 4: 0 };
    updatedSchedule.forEach(c => {
      const size = Math.min(c.students.length, 4);
      courseSizes[size]++;
    });

    console.log(`\n🎉 FILL-FIRST ALGORITHM Complete in ${totalTime.toFixed(0)}ms`);
    console.log(`\n📊 Results by Phase:`);
    console.log(`   Phase 0 (Critical reserve):  ${stats.phase0} students`);
    console.log(`   Phase 1 (Full courses):      ${stats.phase1} students`);
    console.log(`   Phase 2 (Fill existing):     ${stats.phase2} students`);
    console.log(`   Phase 3 (Smaller courses):   ${stats.phase3} students`);
    console.log(`   Phase 4 (Adjacent mixing):   ${stats.phase4} students`);
    console.log(`   Phase 5 (Singles):           ${stats.phase5} students`);
    console.log(`   Phase 6 (Consolidation):     ${stats.phase6Moved} students moved, ${stats.phase6Eliminated} courses eliminated`);
    console.log(`   Total Assigned:              ${totalAssigned}/${students.length} (${(totalAssigned/students.length*100).toFixed(1)}%)`);
    console.log(`   Unassigned:                  ${studentsNotSet.length}`);
    console.log(`\n📊 Course Size Distribution:`);
    console.log(`   4+ students: ${courseSizes[4]} courses`);
    console.log(`   3 students:  ${courseSizes[3]} courses`);
    console.log(`   2 students:  ${courseSizes[2]} courses`);
    console.log(`   1 student:   ${courseSizes[1]} courses`);
    console.log(`   Total:       ${updatedSchedule.length} courses`);
    console.log(`   Efficiency:  ${((courseSizes[4] + courseSizes[3])/updatedSchedule.length*100).toFixed(1)}% courses have 3-4 students\n`);

    setSchedule(updatedSchedule);
    setStudents(students);

    // Save to database using new assignments API
    const saveStart = performance.now();
    const updates = students.map(s => {
      if (assignedStudents.has(s._id)) {
        // Student is assigned - replace with single assignment
        return axios.put(`/api/students/${s._id}/assignments/replace`, {
          day: s.day,
          hour: s.hour,
          coach: s.coach || null
        });
      } else {
        // Student is unassigned - clear all assignments
        return axios.put(`/api/students/${s._id}/assignments/replace`, {
          day: null,
          hour: null,
          coach: null
        });
      }
    });
    await Promise.all(updates);

    console.log(`✅ Saved ${updates.length} updates to database in ${(performance.now() - saveStart).toFixed(0)}ms\n`);

    toast.success(`Zeitplan optimiert! ${totalAssigned}/${students.length} Schüler zugewiesen`);
    return studentsNotSet;

  } catch (error) {
    console.error("Fehler beim Zurücksetzen des Zeitplans:", error);
    toast.error("Fehler beim Zurücksetzen des Zeitplans");
    return [];
  }
};
